import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';

export interface DynamicJsonFormProps {
  schema: any;
  value: Record<string, any>;
  onChange: (value: Record<string, any>) => void;
  disabled?: boolean;
  /**
   * Translation prefix for the schema's fields, e.g.
   * `documentSources.schema.sharepoint`. Labels, helper texts and option
   * names are looked up as `<prefix>.<field>.label`, `<prefix>.<field>.help`
   * and `<prefix>.<field>.options.<value>`; the schema's own title and
   * description remain the fallback, so an untranslated plugin still renders.
   */
  i18nPrefix?: string;
  /** Names of password fields that already hold a stored value. */
  secretsSet?: string[];
  /** Helper text for a stored secret whose field is left empty. */
  storedSecretHint?: string;
}

/**
 * Renders MUI fields from a JSON Schema object: string, number, integer,
 * boolean, enum (as a select) and password-formatted strings. Short fields
 * share a row, fields with long explanations or secrets take the full width.
 */
const DynamicJsonForm: React.FC<DynamicJsonFormProps> = ({
  schema,
  value,
  onChange,
  disabled = false,
  i18nPrefix,
  secretsSet = [],
  storedSecretHint,
}) => {
  const { t } = useTranslation();

  if (schema?.type !== 'object' || !schema.properties) {
    return null;
  }

  const properties = schema.properties;
  const required: string[] = schema.required || [];

  const tx = (suffix: string, fallback: string | undefined): string => {
    if (!i18nPrefix) {
      return fallback ?? '';
    }
    return t(`${i18nPrefix}.${suffix}`, { defaultValue: fallback ?? '' });
  };

  const handleFieldChange = (key: string, fieldValue: any) => {
    onChange({
      ...value,
      [key]: fieldValue,
    });
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {Object.entries(properties).map(([key, prop]: [string, any]) => {
          const isRequired = required.includes(key);
          const fieldLabel = tx(`${key}.label`, prop.title || key);
          const fieldDescription = tx(`${key}.help`, prop.description) || undefined;
          const fieldValue = value[key] ?? prop.default ?? '';
          const isPassword = prop.format === 'password' || key.toLowerCase().includes('secret') || key.toLowerCase().includes('password');
          const isLong = Boolean(prop.maxLength > 200 || prop.type === 'textarea');
          const fullWidth = isPassword || isLong || (fieldDescription ?? '').length > 90 || prop.type === 'boolean';
          const size = { xs: 12, md: fullWidth ? 12 : 6 };

          if (prop.type === 'boolean') {
            return (
              <Grid size={size} key={key}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(fieldValue)}
                      onChange={(e) => handleFieldChange(key, e.target.checked)}
                      disabled={disabled}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">{fieldLabel}</Typography>
                      {fieldDescription && (
                        <Typography variant="caption" color="text.secondary">
                          {fieldDescription}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </Grid>
            );
          }

          if (Array.isArray(prop.enum) && prop.enum.length > 0) {
            const labelId = `dyn-${key}-label`;
            return (
              <Grid size={size} key={key}>
                <FormControl fullWidth required={isRequired} disabled={disabled}>
                  <InputLabel id={labelId}>{fieldLabel}</InputLabel>
                  <Select
                    labelId={labelId}
                    label={fieldLabel}
                    value={fieldValue}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                  >
                    {prop.enum.map((option: string | number) => (
                      <MenuItem key={String(option)} value={option}>
                        {tx(`${key}.options.${String(option)}`, String(option))}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldDescription && <FormHelperText>{fieldDescription}</FormHelperText>}
                </FormControl>
              </Grid>
            );
          }

          if (prop.type === 'integer' || prop.type === 'number') {
            return (
              <Grid size={size} key={key}>
                <TextField
                  fullWidth
                  label={fieldLabel}
                  type="number"
                  value={fieldValue}
                  onChange={(e) => handleFieldChange(key, e.target.value === '' ? undefined : Number(e.target.value))}
                  required={isRequired}
                  helperText={fieldDescription}
                  disabled={disabled}
                />
              </Grid>
            );
          }

          return (
            <Grid size={size} key={key}>
              <TextField
                fullWidth
                label={fieldLabel}
                type={isPassword ? 'password' : 'text'}
                value={fieldValue}
                onChange={(e) => handleFieldChange(key, e.target.value)}
                required={isRequired && !(isPassword && secretsSet.includes(key))}
                placeholder={isPassword && secretsSet.includes(key) && !fieldValue ? '********' : undefined}
                helperText={isPassword && secretsSet.includes(key) && !fieldValue && storedSecretHint ? storedSecretHint : fieldDescription}
                disabled={disabled}
                multiline={isLong}
                rows={isLong ? 3 : 1}
                autoComplete={isPassword ? 'new-password' : 'off'}
              />
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default DynamicJsonForm;
